/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * DR failover and failback report (the portal's server, server/api/DrReportApi.ts):
 *
 *   GET /api/dr-report/workloads/<id>.pdf | .json
 *
 * Built with the caller's Swift session (ApiCaller sends X-Auth-Token), so RBAC applies; the
 * audit trail is in it for administrators only.
 */

import { saveAs } from "file-saver";

import Api from "@src/utils/ApiCaller";

import type { DrReport } from "../../server/drreport/types";

export type { DrReport };

const fileNameFrom = (disposition: string | undefined, fallback: string) => {
  const m = /filename="?([^";]+)"?/i.exec(disposition || "");
  return m ? m[1] : fallback;
};

/** The message of a failed report request ({"error": {"message"}} as JSON, possibly inside a Blob). */
export const reportErrorText = async (err: any): Promise<string> => {
  const status = err?.status;
  let data = err?.data;
  try {
    if (data && typeof Blob !== "undefined" && data instanceof Blob) {
      data = await data.text();
    }
    if (typeof data === "string" && data.trim().startsWith("{")) {
      data = JSON.parse(data);
    }
  } catch (e) {
    // keep what we have
  }
  const message = data?.error?.message;
  if (message) return String(message);
  if (status === 401) return "Your SwiftIQ session has expired: sign in again.";
  if (status === 403)
    return "You are not allowed to read this VM's disaster recovery records.";
  if (status === 404) return "SwiftIQ could not find this protected VM.";
  if (status) return `The DR report could not be built (HTTP ${status}).`;
  return "The DR report could not be built: no answer from the portal's server.";
};

class DrReportSource {
  /** The report's URL; `inline` shows it in the browser instead of downloading it. */
  pdfUrl(workloadId: string, inline = false): string {
    return `/api/dr-report/workloads/${encodeURIComponent(workloadId)}.pdf${inline ? "?inline=1" : ""}`;
  }

  /** Builds the report and saves it as swift-dr-report-<name>-<date>.pdf. */
  async download(
    workloadId: string,
  ): Promise<{
    fileName: string;
    reportId: string | null;
    sha256: string | null;
  }> {
    let response;
    try {
      response = await Api.send({
        url: this.pdfUrl(workloadId),
        responseType: "blob",
        timeout: 180000,
        quietError: true,
      });
    } catch (err: any) {
      throw new Error(await reportErrorText(err));
    }
    const fileName = fileNameFrom(
      response.headers?.["content-disposition"],
      `swift-dr-report-${workloadId}.pdf`,
    );
    saveAs(response.data, fileName);
    return {
      fileName,
      reportId: response.headers?.["x-report-id"] || null,
      sha256: response.headers?.["x-report-sha256"] || null,
    };
  }

  /** The client's DR service report for the last `days` days (server/servicereport), saved as a PDF. */
  async downloadServiceReport(days = 30): Promise<{ fileName: string; reportId: string | null; sha256: string | null }> {
    const end = new Date();
    const to = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate() + 1));
    const from = new Date(to.getTime() - days * 86400000);
    const day = (d: Date) => d.toISOString().slice(0, 10);
    let response;
    try {
      response = await Api.send({
        url: `/api/dr-service-report.pdf?from=${day(from)}&to=${day(to)}`,
        responseType: "blob",
        timeout: 180000,
        quietError: true,
      });
    } catch (err: any) {
      throw new Error(await reportErrorText(err));
    }
    const fileName = fileNameFrom(response.headers?.["content-disposition"], "swift-dr-service-report.pdf");
    saveAs(response.data, fileName);
    return { fileName, reportId: response.headers?.["x-report-id"] || null, sha256: response.headers?.["x-report-sha256"] || null };
  }
}

export default new DrReportSource();
